import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, posService, billingService, restaurantService } from '../../services/dataService';
import { computeBill } from '../../utils/billingCalc';
import { realtimeService } from '../../services/realtimeService';
import { pullNow, seedOrderNow } from '../../services/syncService';
import KOTPrint from './KOTPrint';
import InvoicePrint from './InvoicePrint';
import CustomerPicker from './CustomerPicker';
import { tzMidnightUTC, todayInTz } from '../../utils/tzUtils';
import { invBillingIntegration } from '../../services/inventoryService';
import { bleOpenCashDrawer } from '../../utils/blePrinter';
import { isCashDrawerEnabled } from './BillingSettings';

// ─── Shared styles ────────────────────────────────────────────────────────────
const card = {
  background: 'rgba(255,255,255,0.95)',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.06)',
};
const btn = (variant = 'primary', disabled = false) => {
  const base = {
    border: 'none', borderRadius: '10px', padding: '10px 20px',
    fontSize: '0.9rem', fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
  };
  if (disabled) return { ...base, background: '#e5e7eb', color: '#9ca3af' };
  if (variant === 'primary') return { ...base, background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', boxShadow: '0 3px 10px rgba(255,68,68,0.25)' };
  if (variant === 'outline') return { ...base, background: 'transparent', color: '#666', border: '2px solid #e5e7eb' };
  if (variant === 'green') return { ...base, background: '#16a34a', color: 'white' };
  if (variant === 'purple') return { ...base, background: '#7c3aed', color: 'white' };
  if (variant === 'danger') return { ...base, background: '#dc2626', color: 'white' };
  return base;
};
const fmt = (n) => `₹${(n || 0).toFixed(2)}`;
const friendlyErr = (err) => {
  if (!err) return 'Something went wrong. Please try again.';
  const msg = String(err.message || err).toLowerCase();
  if (msg.includes('fetch') || msg.includes('network')) return 'Network error — check your internet connection.';
  if (msg.includes('jwt') || msg.includes('expired') || msg.includes('auth')) return 'Session expired — please refresh and log in again.';
  if (msg.includes('policy') || msg.includes('permission') || msg.includes('rls')) return "You don't have permission to do this.";
  if (msg.includes('duplicate') || msg.includes('unique')) return 'This record already exists.';
  if (msg.includes('foreign key') || msg.includes('violates foreign')) return 'This record is in use and cannot be removed.';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Request timed out — please try again.';
  return 'Something went wrong. Please try again.';
};
const fmtDateTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' · ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

// ─── Order card inside table/counter detail view ──────────────────────────────
const PAYMENT_METHODS = ['cash', 'card', 'upi', 'other'];

const OrderCard = ({ order, onKOT, onReprint, onCollectPayment, onDelete, onMarkInvalid, splitMode, group, onGroupChange, maxGroups = 3 }) => {
  const time = fmtDateTime(order.created_at);
  const itemCount = (order.order_items || []).reduce((s, i) => s + i.quantity, 0);
  const isBilled = !!order.invoice_number;
  const isPaid = !!order.paid_at;
  const isVoid = !!order.is_invalid;

  const [showCollect, setShowCollect] = React.useState(false);
  const [payMethod, setPayMethod] = React.useState('cash');
  const [paying, setPaying] = React.useState(false);

  const handleCollect = async () => {
    setPaying(true);
    await onCollectPayment(order.id, payMethod);
    setPaying(false);
    setShowCollect(false);
  };

  return (
    <div style={{
      border: isVoid ? '1px solid #fca5a5' : isPaid ? '1px solid #bbf7d0' : isBilled ? '1px solid #fed7aa' : '1px solid #e5e7eb',
      borderRadius: '12px', padding: '14px 16px', marginBottom: '10px',
      background: isVoid ? '#fff5f5' : isPaid ? '#f0fdf4' : isBilled ? '#fffbeb' : 'white',
      opacity: isVoid ? 0.7 : 1,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#333' }}>{order.order_number}</span>
          <span style={{ fontSize: '0.78rem', color: '#888' }}>{time}</span>
          {isVoid && (
            <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>
              INVALID
            </span>
          )}
          {!isVoid && isPaid && (
            <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>
              PAID · {(order.payment_method || 'paid').toUpperCase()}
            </span>
          )}
          {!isVoid && isBilled && !isPaid && (
            <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>
              AWAITING PAYMENT
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {splitMode && !isBilled && (
            <select
              value={group}
              onChange={e => onGroupChange(order.id, parseInt(e.target.value))}
              style={{ padding: '4px 8px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', outline: 'none' }}
            >
              {Array.from({ length: maxGroups }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Invoice {n}</option>
              ))}
            </select>
          )}
          {!isBilled && !isVoid && (
            <button onClick={() => onKOT(order)} style={{ ...btn('purple'), padding: '6px 14px', fontSize: '0.8rem' }}>
              Print KOT
            </button>
          )}
          {!isBilled && !isVoid && onDelete && (
            <button
              onClick={() => { if (window.confirm('Delete this order? This cannot be undone.')) onDelete(order.id); }}
              style={{ ...btn('danger'), padding: '6px 14px', fontSize: '0.8rem' }}
            >Delete</button>
          )}
          {isBilled && !isVoid && (
            <button onClick={() => onReprint(order)} style={{ ...btn('outline'), padding: '6px 14px', fontSize: '0.8rem' }}>
              Reprint Invoice
            </button>
          )}
          {isBilled && !isVoid && onMarkInvalid && (
            <button
              onClick={() => { if (window.confirm(`Mark invoice ${order.invoice_number} as invalid? This cannot be undone.`)) onMarkInvalid(order.id); }}
              style={{ background: 'transparent', border: '1.5px solid #fca5a5', borderRadius: '8px', padding: '6px 14px', fontSize: '0.8rem', color: '#dc2626', cursor: 'pointer', fontWeight: '700' }}
            >Mark as Invalid</button>
          )}
        </div>
      </div>

      {/* Items */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {(order.order_items || []).map((oi, idx) => (
          <span key={oi.id || idx} style={{
            background: '#f3f4f6', borderRadius: '6px',
            padding: '3px 8px', fontSize: '0.82rem', color: '#444',
          }}>
            {oi.menu_items?.name || 'Item'} ×{oi.quantity}
          </span>
        ))}
      </div>
      {order.notes && (
        <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
          Note: {order.notes}
        </div>
      )}
      <div style={{ marginTop: '8px', fontSize: '0.82rem', color: '#888' }}>
        {itemCount} item{itemCount !== 1 ? 's' : ''} · {fmt(order.total_amount)}
      </div>

      {/* Collect payment — shown on billed but unpaid orders (not voided) */}
      {isBilled && !isPaid && !isVoid && (
        <div style={{ marginTop: '10px' }}>
          {!showCollect ? (
            <button onClick={() => setShowCollect(true)} style={{ ...btn('green'), padding: '7px 16px', fontSize: '0.82rem' }}>
              Collect Payment
            </button>
          ) : (
            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Select Payment Method</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {PAYMENT_METHODS.map(m => (
                  <button key={m} onClick={() => setPayMethod(m)} style={{
                    padding: '6px 14px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600',
                    border: payMethod === m ? 'none' : '2px solid #e5e7eb',
                    background: payMethod === m ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'white',
                    color: payMethod === m ? 'white' : '#666',
                    cursor: 'pointer', textTransform: 'capitalize',
                  }}>{m}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleCollect} disabled={paying}
                  style={{ ...btn('green', paying), padding: '7px 16px', fontSize: '0.82rem' }}>
                  {paying ? 'Processing...' : 'Confirm Payment'}
                </button>
                <button onClick={() => setShowCollect(false)} style={{ ...btn('outline'), padding: '7px 16px', fontSize: '0.82rem' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const POSDashboard = ({ restaurant, managerId, billingSettingsProp }) => {
  const navigate = useNavigate();
  // ── Data state ──────────────────────────────────────────────────────────────
  const [allOrders, setAllOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [menu, setMenu] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null);
  const [billingDefaults, setBillingDefaults] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Navigation ──────────────────────────────────────────────────────────────
  // view: 'dashboard' | { type: 'table', tableId } | { type: 'counter' } | { type: 'add-order', context }
  const [view, setView] = useState('dashboard');

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [kotOrder, setKotOrder] = useState(null);
  const [invoicePrint, setInvoicePrint] = useState(null); // { order, mergedOrderNumbers }

  const handleKOT = async (order) => {
    const { kotNumber } = await posService.claimKotNumber(order.id);
    setKotOrder({ ...order, kot_number: kotNumber ?? order.order_number ?? undefined });
  };

  // ── Edit payment method ───────────────────────────────────────────────────
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  // ── Split invoice state ──────────────────────────────────────────────────────
  const [splitMode, setSplitMode] = useState(false);
  const [orderGroups, setOrderGroups] = useState({}); // orderId → groupNumber (1/2/3)

  // ── Billing modal ───────────────────────────────────────────────────────────
  const [billingModal, setBillingModal] = useState(null); // { groups: [{orders, groupNum}] }
  const [billingState, setBillingState] = useState({}); // groupNum → { paymentMethod, discountType, discountValue, processing }
  // Ref so closeInvoicePrint always reads the latest billingState (avoids stale closure)
  const billingStateRef = useRef({});
  billingStateRef.current = billingState;

  // ── Incoming QR order notification ──────────────────────────────────────────
  const [newOrderAlert, setNewOrderAlert] = useState(false);

  // ── Add order sub-view state ────────────────────────────────────────────────
  const [addOrderCart, setAddOrderCart] = useState([]);
  const [addOrderNotes, setAddOrderNotes] = useState('');
  const [addOrderSearch, setAddOrderSearch] = useState('');
  const [addOrderCategory, setAddOrderCategory] = useState('');
  const [addOrderPlacing, setAddOrderPlacing] = useState(false);
  const [addOrderError, setAddOrderError] = useState('');

  // ── Load data ────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!restaurant?.id) return;
    const [ordersRes, tablesRes, menuRes, settingsRes, defaultsRes] = await Promise.all([
      posService.getCounterOrders(restaurant.id, { days: 2 }),
      restaurantService.getTables(restaurant.id),
      restaurantService.getMenu(restaurant.id),
      billingService.getSettings(restaurant.id),
      billingService.getBillingDefaults(restaurant.id),
    ]);
    setAllOrders(ordersRes.data || []);
    setTables(tablesRes.data || []);
    setMenu(menuRes.data || []);
    setBillingSettings(settingsRes.data);
    setBillingDefaults(defaultsRes.data);
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Keep billingSettings in sync when parent updates it (e.g. after BillingSettings save)
  useEffect(() => { if (billingSettingsProp) setBillingSettings(billingSettingsProp); }, [billingSettingsProp]);

  // ── Refresh orders only (after placing/billing) ───────────────────────────────
  const refreshOrders = useCallback(async () => {
    if (!restaurant?.id) return;
    const res = await posService.getCounterOrders(restaurant.id, { days: 2 });
    setAllOrders(res.data || []);
  }, [restaurant?.id]);


  // ── Realtime broadcast: fires when orders are placed or status changes ───────
  const prevIncomingCountRef = useRef(0);
  useEffect(() => {
    if (!restaurant?.id) return;
    const channel = supabase
      .channel(`restaurant-${restaurant.id}`)
      .on('broadcast', { event: 'new_order' }, async () => {
        setNewOrderAlert(true);
        if (window.menuronDesktop?.isDesktop) {
          await pullNow();  // fetch from Supabase (with items) → seed SQLite
        }
        refreshOrders();
      })
      .on('broadcast', { event: 'order_status_update' }, async () => {
        // Desktop: do NOT call pullNow() here — local status changes (confirm/bill/pay)
        // write to SQLite only; Supabase lags behind and would overwrite the local state.
        // Remote status changes are already handled by postgres_changes UPDATE → seedOrderNow.
        refreshOrders();
      })
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders', filter: `restaurant_id=eq.${restaurant.id}` },
        async (payload) => {
          setNewOrderAlert(true);
          if (window.menuronDesktop?.isDesktop) {
            if (payload?.new?.id) {
              // Instant: seed from payload (no Supabase round-trip) → card appears immediately
              await seedOrderNow(payload.new);
            } else {
              await pullNow();
            }
          }
          refreshOrders();
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: `restaurant_id=eq.${restaurant.id}` },
        async (payload) => {
          if (window.menuronDesktop?.isDesktop) {
            if (payload?.new?.id) await seedOrderNow(payload.new);
            else await pullNow();
          }
          refreshOrders();
        }
      )
      .subscribe((status, err) => {
        console.log('[POS] realtime', status, err?.message || '');
      });
    return () => { supabase.removeChannel(channel); };
  }, [restaurant?.id, refreshOrders]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  // today's midnight in the restaurant's timezone
  const tz = restaurant?.timezone || 'Asia/Kolkata';
  const todayStart = tzMidnightUTC(todayInTz(tz), tz);

  // ── Derived: incoming orders awaiting manager action (today only) ──────────
  // QR pending = awaiting waiter; waiter_confirmed = waiter confirmed QR order OR waiter placed order
  const incomingOrders = allOrders.filter(o =>
    new Date(o.created_at) >= todayStart && (
      (o.order_source === 'QR' && o.status === 'pending') ||
      o.status === 'waiter_confirmed'
    )
  );

  // Detect when incoming order count rises → trigger alert
  useEffect(() => {
    if (incomingOrders.length > prevIncomingCountRef.current) {
      setNewOrderAlert(true);
    }
    prevIncomingCountRef.current = incomingOrders.length;
  }, [incomingOrders.length]);

  // Detect if incoming orders grid overflows 2 rows → show "View All"
  const incomingScrollRef = useRef(null);
  const [incomingOverflows, setIncomingOverflows] = useState(false);
  useEffect(() => {
    const el = incomingScrollRef.current;
    if (!el) return;
    const check = () => setIncomingOverflows(el.scrollHeight > el.clientHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [incomingOrders.length]);

  // Active orders = not merged/cancelled, excludes QR-pending (awaiting waiter confirmation)
  // waiter_confirmed orders show on table AND in incoming simultaneously
  const activeOrders = allOrders.filter(o =>
    o.status !== 'merged' &&
    o.status !== 'cancelled' &&
    !(o.order_source === 'QR' && o.status === 'pending')
  );

  // Group active orders by table
  const tableOrdersMap = {};
  for (const o of activeOrders) {
    if (o.table_id) {
      if (!tableOrdersMap[o.table_id]) tableOrdersMap[o.table_id] = [];
      tableOrdersMap[o.table_id].push(o);
    }
  }

  // Counter orders = active + no table
  const counterOrders = activeOrders.filter(o => !o.table_id);

  // ── Counter sessions (grouped by counter_session_id, continuous across days) ─
  const sessionOrdersMap = {};
  for (const o of counterOrders) {
    if (o.counter_session_id) {
      if (!sessionOrdersMap[o.counter_session_id]) sessionOrdersMap[o.counter_session_id] = [];
      sessionOrdersMap[o.counter_session_id].push(o);
    }
  }
  // Sessions ordered by their earliest order's created_at
  const orderedSessions = Object.keys(sessionOrdersMap).sort((a, b) => {
    const aMin = Math.min(...sessionOrdersMap[a].map(o => new Date(o.created_at).getTime()));
    const bMin = Math.min(...sessionOrdersMap[b].map(o => new Date(o.created_at).getTime()));
    return aMin - bMin;
  });
  const sessionNumberMap = {};
  orderedSessions.forEach((id, idx) => { sessionNumberMap[id] = idx + 1; });

  // Tables with orders

  // ── Menu items for add-order view ────────────────────────────────────────────
  const allMenuItems = menu.flatMap(cat =>
    (cat.menu_items || [])
      .filter(i => !i.deleted && i.is_available !== false)
      .map(i => ({ ...i, categoryName: cat.name }))
  );
  const menuCategories = [...new Set(allMenuItems.map(i => i.categoryName))];
  const filteredMenuItems = allMenuItems.filter(i => {
    const matchesSearch = !addOrderSearch.trim() || i.name.toLowerCase().includes(addOrderSearch.toLowerCase());
    const matchesCat = !addOrderCategory || i.categoryName === addOrderCategory;
    return matchesSearch && matchesCat;
  });

  // ── Add to cart ───────────────────────────────────────────────────────────────
  const addToCart = (item) => {
    setAddOrderCart(prev => {
      const ex = prev.find(c => c.menu_item_id === item.id);
      if (ex) return prev.map(c => c.menu_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menu_item_id: item.id, name: item.name, unit_price: item.price, quantity: 1 }];
    });
  };
  const updateCartQty = (menuItemId, qty) => {
    if (qty <= 0) setAddOrderCart(prev => prev.filter(c => c.menu_item_id !== menuItemId));
    else setAddOrderCart(prev => prev.map(c => c.menu_item_id === menuItemId ? { ...c, quantity: qty } : c));
  };
  const cartTotal = addOrderCart.reduce((s, c) => s + c.unit_price * c.quantity, 0);

  // ── Place order ───────────────────────────────────────────────────────────────
  const placeOrder = async () => {
    if (addOrderCart.length === 0) return;
    setAddOrderPlacing(true);
    setAddOrderError('');
    const context = view.context || {};
    const { error } = await posService.createCounterOrder(restaurant.id, {
      tableId: context.tableId || null,
      counterSessionId: context.sessionId || null,
      orderType: context.tableId ? 'DINE_IN' : 'TAKEAWAY',
      items: addOrderCart,
      notes: addOrderNotes,
      restaurantName: restaurant.name,
    });
    if (error) { setAddOrderPlacing(false); setAddOrderError(friendlyErr(error)); return; }
    await refreshOrders();
    setAddOrderCart([]);
    setAddOrderNotes('');
    setAddOrderSearch('');
    setAddOrderCategory('');
    // Go back to where we came from
    if (context.tableId) setView({ type: 'table', tableId: context.tableId });
    else if (context.sessionId) setView({ type: 'counter-session', sessionId: context.sessionId });
    else setView('dashboard');
  };

  // ── Split invoice helpers ─────────────────────────────────────────────────────
  const initSplitMode = (orders) => {
    const groups = {};
    orders.forEach(o => { groups[o.id] = 1; });
    setOrderGroups(groups);
    setSplitMode(true);
  };
  const cancelSplit = () => { setSplitMode(false); setOrderGroups({}); };
  const setGroup = (orderId, groupNum) => {
    setOrderGroups(prev => ({ ...prev, [orderId]: groupNum }));
  };

  // ── Build billing groups from orders ─────────────────────────────────────────
  const buildBillingGroups = (unbilledOrders) => {
    if (splitMode) {
      // Collect unique group numbers actually used
      const usedGroups = [...new Set(Object.values(orderGroups))].sort();
      return usedGroups.map(gNum => ({
        groupNum: gNum,
        orders: unbilledOrders.filter(o => (orderGroups[o.id] || 1) === gNum),
      })).filter(g => g.orders.length > 0);
    }
    return [{ groupNum: 1, orders: unbilledOrders }];
  };

  // ── Open billing modal ────────────────────────────────────────────────────────
  const openBillingModal = (allTableOrders) => {
    const unbilled = allTableOrders.filter(o => !o.invoice_number && !o.is_invalid);
    if (unbilled.length === 0) return;
    const groups = buildBillingGroups(unbilled);
    const initState = {};
    groups.forEach(g => {
      initState[g.groupNum] = {
        discountType: billingDefaults?.defaultDiscountEnabled ? billingDefaults.defaultDiscountType : 'NONE',
        discountValue: billingDefaults?.defaultDiscountEnabled ? billingDefaults.defaultDiscountValue : 0,
        gstEnabled: billingDefaults?.gstEnabled ?? false,
        gstPercent: billingDefaults?.gstPercent ?? 0,
        serviceChargeEnabled: billingDefaults?.serviceChargeEnabled ?? false,
        serviceChargePercent: billingDefaults?.serviceChargePercent ?? 0,
        paymentMethod: 'cash',
        processing: false,
        error: '',
        selectedCustomer: null,
      };
    });
    setBillingModal({ groups });
    setBillingState(initState);
  };

  // ── Compute totals for a group ────────────────────────────────────────────────
  const computeGroupBill = (groupOrders, groupState) => {
    const subtotal = groupOrders.reduce((s, o) => s + (o.total_amount || 0), 0);
    const { discountType, discountValue, gstEnabled, gstPercent, serviceChargeEnabled, serviceChargePercent } = groupState;
    const gstPct = gstEnabled ? (gstPercent || 0) : 0;
    const scPct = serviceChargeEnabled ? (serviceChargePercent || 0) : 0;
    return { subtotal, ...computeBill(subtotal, discountType, discountValue, gstPct, scPct) };
  };

  // ── Process one invoice group ─────────────────────────────────────────────────
  const processGroup = async (group, groupState) => {
    const { orders: groupOrders, groupNum } = group;
    if (groupOrders.length === 0) return null;

    setBillingState(prev => ({ ...prev, [groupNum]: { ...prev[groupNum], processing: true, error: '' } }));

    const { invoiceNumber, error: invErr } = await billingService.claimNextInvoiceNumber(restaurant.id, restaurant.timezone || 'Asia/Kolkata');
    if (invErr) {
      setBillingState(prev => ({ ...prev, [groupNum]: { ...prev[groupNum], processing: false, error: 'Failed to claim invoice number' } }));
      return null;
    }

    const bill = computeGroupBill(groupOrders, groupState);
    const gstPct = groupState.gstEnabled ? (groupState.gstPercent || 0) : 0;
    const scPct = groupState.serviceChargeEnabled ? (groupState.serviceChargePercent || 0) : 0;
    const isOverride = (
      groupState.gstEnabled !== (billingDefaults?.gstEnabled ?? false) ||
      groupState.gstPercent !== (billingDefaults?.gstPercent ?? 0) ||
      groupState.serviceChargeEnabled !== (billingDefaults?.serviceChargeEnabled ?? false) ||
      groupState.serviceChargePercent !== (billingDefaults?.serviceChargePercent ?? 0) ||
      groupState.discountType !== (billingDefaults?.defaultDiscountEnabled ? billingDefaults.defaultDiscountType : 'NONE') ||
      groupState.discountValue !== (billingDefaults?.defaultDiscountEnabled ? billingDefaults.defaultDiscountValue : 0)
    );

    const primaryOrder = groupOrders[0];
    const secondaryOrders = groupOrders.slice(1);

    // Merge all order_items for the primary
    const mergedItems = groupOrders.flatMap(o => o.order_items || []);
    if (groupOrders.length > 1) {
      await posService.updateOrderItems(primaryOrder.id, mergedItems.map(oi => ({
        menu_item_id: oi.menu_items?.id || oi.menu_item_id,
        quantity: oi.quantity,
        unit_price: oi.unit_price,
      })));
    }

    const autoPaid = groupState.paymentMethod && groupState.paymentMethod !== 'unpaid';
    const { data: billedOrder, error: billErr } = await posService.billOrder(primaryOrder.id, {
      taxableAmount: bill.taxableAmount,
      discountAmount: bill.discountAmount,
      discountType: groupState.discountType,
      gstAmount: bill.gstAmount,
      serviceChargeAmount: bill.serviceChargeAmount,
      totalPayable: bill.totalPayable,
      invoiceNumber,
      appliedGstPercent: gstPct,
      appliedScPercent: scPct,
      isOverride,
      paymentMethod: autoPaid ? groupState.paymentMethod : null,
      customerId: groupState.selectedCustomer?.id || null,
      customerName: groupState.selectedCustomer?.name || null,
      customerPhone: groupState.selectedCustomer?.phone || null,
      managerId: managerId || null,
    });

    if (billErr) {
      setBillingState(prev => ({ ...prev, [groupNum]: { ...prev[groupNum], processing: false, error: 'Billing failed' } }));
      return null;
    }

    // Deduct inventory stock + auto-log recipe wastage for all orders in this group
    for (const order of [primaryOrder, ...secondaryOrders]) {
      invBillingIntegration.deductForOrder(restaurant.id, order.id).catch(() => {});
    }

    // Auto-open cash drawer on cash payment (fire-and-forget)
    if (autoPaid && groupState.paymentMethod === 'cash' && isCashDrawerEnabled()) {
      bleOpenCashDrawer().catch(() => {});
    }

    // Mark secondary orders as merged
    for (const sec of secondaryOrders) {
      await posService.markAsMerged(sec.id, invoiceNumber);
    }

    setBillingState(prev => ({ ...prev, [groupNum]: { ...prev[groupNum], processing: false, billed: true, autoPaid } }));

    // Build a merged order object for InvoicePrint
    // billedOrder already has paid_at + payment_method set when autoPaid (single DB update)
    const mergedOrderObj = {
      ...billedOrder,
      order_items: mergedItems,
      restaurant_tables: primaryOrder.restaurant_tables,
    };
    const mergedOrderNumbers = secondaryOrders.map(o => o.order_number);

    return { order: mergedOrderObj, mergedOrderNumbers };
  };

  // ── Process and print a single invoice group ─────────────────────────────────
  const processAndPrintGroup = async (group) => {
    const gs = billingState[group.groupNum];
    const result = await processGroup(group, gs);
    if (result) {
      await refreshOrders();
      setInvoicePrint(result);
    }
  };

  // ── Accept an incoming QR order ──────────────────────────────────────────────
  const confirmOrder = async (orderId) => {
    await posService.confirmOrder(orderId);
    setNewOrderAlert(false);
    await refreshOrders();
    realtimeService.broadcastOrderStatusUpdate(restaurant.id, orderId);
  };

  const acknowledgeAll = async () => {
    await Promise.all(incomingOrders.map(o => posService.confirmOrder(o.id)));
    setNewOrderAlert(false);
    await refreshOrders();
    incomingOrders.forEach(o => realtimeService.broadcastOrderStatusUpdate(restaurant.id, o.id));
  };

  // ── Collect payment for a billed order ───────────────────────────────────────
  const collectPayment = async (orderId, paymentMethod) => {
    await posService.markPaid(orderId, paymentMethod);
    await refreshOrders();
  };

  // ── Delete an unbilled order ──────────────────────────────────────────────────
  const deleteOrder = async (orderId) => {
    await posService.deleteOrder(orderId);
    await refreshOrders();
  };

  // ── Mark a billed order/invoice as void ───────────────────────────────────────
  const markOrderInvalid = async (orderId) => {
    await posService.markOrderInvalid(orderId);
    await refreshOrders();
  };

  // ── Update payment method on a paid order ─────────────────────────────────
  const changePaymentMethod = async (orderId, method) => {
    await posService.updatePaymentMethod(orderId, method);
    setEditingPaymentId(null);
    await refreshOrders();
  };

  // ── Close invoice print: auto-close billing modal when all groups billed ─────
  // Uses billingStateRef (not billingState) to avoid stale-closure reading old values.
  const closeInvoicePrint = () => {
    setInvoicePrint(null);
    if (billingModal) {
      const allBilled = billingModal.groups.every(g => billingStateRef.current[g.groupNum]?.billed);
      if (allBilled) {
        setBillingModal(null);
        setSplitMode(false);
        setOrderGroups({});
      }
    }
  };

  // ── Current view's orders (table or counter-session) ─────────────────────────
  const currentDetailOrders = view?.type === 'table'
    ? activeOrders.filter(o => o.table_id === view.tableId)
    : view?.type === 'counter-session'
      ? (sessionOrdersMap[view.sessionId] || [])
      : [];
  const currentDetailName = view?.type === 'table'
    ? (() => { const t = tables.find(t => t.id === view.tableId); return t ? (t.custom_name || `Table ${t.table_number}`) : 'Table'; })()
    : view?.type === 'counter-session'
      ? `Counter ${sessionNumberMap[view.sessionId] || '?'}`
      : '';

  // ── Unbilled count ────────────────────────────────────────────────────────────
  const unbilledCount = currentDetailOrders.filter(o => !o.invoice_number && !o.is_invalid).length;

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading...</div>;

  // ─────────────────────────────────────────────────────────────────────────────
  // VIEWS
  // ─────────────────────────────────────────────────────────────────────────────

  // ── All Incoming Orders View ─────────────────────────────────────────────────
  if (view?.type === 'incoming-orders') {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <button onClick={() => setView('dashboard')} style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
            ← Back
          </button>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#333' }}>
            Incoming Orders ({incomingOrders.length})
          </h1>
          {incomingOrders.length > 1 && (
            <button onClick={acknowledgeAll} style={{ ...btn('green'), padding: '8px 18px', fontSize: '0.85rem', marginLeft: 'auto' }}>
              Acknowledge All
            </button>
          )}
        </div>
        {incomingOrders.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#bbb', padding: '60px', fontSize: '0.95rem' }}>
            No incoming orders.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
            {incomingOrders.map(order => {
              const tableName = order.restaurant_tables
                ? (order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`)
                : (order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Unknown Table');
              const time = fmtDateTime(order.created_at);
              const items = order.order_items || [];
              const waiterConfirmed = order.status === 'waiter_confirmed';
              const waiterPlaced = order.order_source === 'WAITER';
              const cardBg = waiterConfirmed ? '#f0fdf4' : '#fff7ed';
              const cardBorder = waiterConfirmed ? '#16a34a' : '#FF6B00';
              return (
                <div key={order.id} style={{
                  background: cardBg,
                  border: `2px solid ${cardBorder}`,
                  borderRadius: '14px', padding: '18px',
                  display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333' }}>{tableName}</div>
                    <div style={{ fontSize: '0.78rem', color: '#888' }}>{time}</div>
                  </div>
                  {waiterConfirmed && (
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#16a34a', marginBottom: '10px' }}>
                      {waiterPlaced ? `Placed by ${order.confirmed_by_name}` : `✓ Confirmed by ${order.confirmed_by_name}`}
                    </div>
                  )}
                  <div style={{ marginBottom: '14px', flex: 1 }}>
                    {items.map((oi, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#444', padding: '3px 0', borderBottom: idx < items.length - 1 ? `1px solid ${waiterConfirmed ? '#bbf7d0' : '#ffe4cc'}` : 'none' }}>
                        <span>{oi.menu_items?.name || 'Item'}</span>
                        <span style={{ fontWeight: '600', color: waiterConfirmed ? '#16a34a' : '#FF6B00' }}>× {oi.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => confirmOrder(order.id)}
                    style={{ ...btn(waiterConfirmed ? 'green' : 'primary'), width: '100%', padding: '10px', fontSize: '0.88rem', marginTop: 'auto' }}
                  >
                    {waiterConfirmed ? 'Acknowledge Order' : 'Confirm Order'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Add Order View ──────────────────────────────────────────────────────────
  if (view?.type === 'add-order') {
    const context = view.context || {};
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => {
            setAddOrderCart([]); setAddOrderNotes(''); setAddOrderSearch(''); setAddOrderCategory('');
            if (context.tableId) setView({ type: 'table', tableId: context.tableId });
            else if (context.sessionId && sessionOrdersMap[context.sessionId]?.length > 0) setView({ type: 'counter-session', sessionId: context.sessionId });
            else setView('dashboard');
          }} style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
            ← Back
          </button>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#333' }}>
            Add Order {context.tableName ? `— ${context.tableName}` : context.sessionId ? `— Counter ${context.sessionNumber || ''}` : '— Counter'}
          </h1>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px', alignItems: 'start' }}>
          {/* Menu picker */}
          <div style={{ ...card, padding: '24px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text" placeholder="Search items..." value={addOrderSearch}
                onChange={e => setAddOrderSearch(e.target.value)}
                style={{ flex: 1, padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#FF6B00'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '16px' }}>
              {/* Category sidebar */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button
                  onClick={() => setAddOrderCategory('')}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: 'none', textAlign: 'left',
                    fontWeight: !addOrderCategory ? '700' : '500', fontSize: '0.85rem', cursor: 'pointer',
                    background: !addOrderCategory ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                    color: !addOrderCategory ? 'white' : '#444',
                  }}
                >All</button>
                {menuCategories.map(cat => (
                  <button key={cat} onClick={() => setAddOrderCategory(cat)} style={{
                    padding: '8px 12px', borderRadius: '8px', border: 'none', textAlign: 'left',
                    fontWeight: addOrderCategory === cat ? '700' : '500', fontSize: '0.85rem', cursor: 'pointer',
                    background: addOrderCategory === cat ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                    color: addOrderCategory === cat ? 'white' : '#444',
                  }}>{cat}</button>
                ))}
              </div>
              {/* Items */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                {filteredMenuItems.map(item => {
                  const inCart = addOrderCart.find(c => c.menu_item_id === item.id);
                  return (
                    <div key={item.id} onClick={() => addToCart(item)} style={{
                      border: inCart ? '2px solid #FF6B00' : '2px solid #e5e7eb',
                      borderRadius: '12px', padding: '12px', cursor: 'pointer',
                      background: inCart ? '#fff7ed' : 'white',
                      position: 'relative', transition: 'all 0.15s',
                    }}>
                      {inCart && (
                        <div style={{
                          position: 'absolute', top: '8px', right: '8px',
                          background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                          color: 'white', borderRadius: '50%', width: '20px', height: '20px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.72rem', fontWeight: '700',
                        }}>{inCart.quantity}</div>
                      )}
                      <div style={{ fontWeight: '600', color: '#333', fontSize: '0.88rem', paddingRight: '22px', marginBottom: '2px' }}>{item.name}</div>
                      <div style={{ fontWeight: '700', color: '#FF4444', fontSize: '0.92rem' }}>{fmt(item.price)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cart */}
          <div style={{ ...card, padding: '24px', position: 'sticky', top: '100px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>Order</h2>
            {addOrderCart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa', fontSize: '0.88rem' }}>
                Tap items to add
              </div>
            ) : (
              <div style={{ marginBottom: '12px' }}>
                {addOrderCart.map(item => (
                  <div key={item.menu_item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ flex: 1, fontSize: '0.85rem', color: '#333', fontWeight: '500' }}>{item.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => updateCartQty(item.menu_item_id, item.quantity - 1)} style={{ width: '22px', height: '22px', border: '1px solid #e5e7eb', borderRadius: '5px', cursor: 'pointer', background: 'white', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                      <span style={{ minWidth: '16px', textAlign: 'center', fontWeight: '700', fontSize: '0.88rem' }}>{item.quantity}</span>
                      <button onClick={() => updateCartQty(item.menu_item_id, item.quantity + 1)} style={{ width: '22px', height: '22px', border: '1px solid #e5e7eb', borderRadius: '5px', cursor: 'pointer', background: 'white', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                      <span style={{ minWidth: '52px', textAlign: 'right', fontWeight: '600', color: '#FF4444', fontSize: '0.85rem' }}>{fmt(item.unit_price * item.quantity)}</span>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontWeight: '700', color: '#333' }}>
                  <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                </div>
              </div>
            )}
            <textarea
              placeholder="Notes (optional)..." value={addOrderNotes}
              onChange={e => setAddOrderNotes(e.target.value)} rows={2}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.88rem', outline: 'none', resize: 'vertical', marginBottom: '12px', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />
            {addOrderError && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '8px' }}>{addOrderError}</div>}
            <button
              onClick={placeOrder}
              disabled={addOrderPlacing || addOrderCart.length === 0}
              style={{ ...btn('primary', addOrderPlacing || addOrderCart.length === 0), width: '100%', padding: '13px' }}
            >
              {addOrderPlacing ? 'Placing...' : `Place Order · ${fmt(cartTotal)}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Table / Counter-Session Detail View ─────────────────────────────────────
  if (view?.type === 'table' || view?.type === 'counter-session') {
    const tableOrders = currentDetailOrders;
    const unpaidOrders = tableOrders.filter(o => !o.paid_at);
    const paidTodayOrders = tableOrders.filter(o => !!o.paid_at);

    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '30px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button onClick={() => { setSplitMode(false); setOrderGroups({}); setView('dashboard'); }}
            style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
            ← Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#333' }}>{currentDetailName}</h1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => { setAddOrderPlacing(false); setAddOrderCart([]); setAddOrderNotes(''); setAddOrderSearch(''); setAddOrderCategory(''); setView({ type: 'add-order', context: view.type === 'table' ? { tableId: view.tableId, tableName: currentDetailName } : { sessionId: view.sessionId, sessionNumber: sessionNumberMap[view.sessionId] } }); }}
              style={{ ...btn('primary'), padding: '8px 16px', fontSize: '0.85rem' }}>
              + Add Order
            </button>
            {unbilledCount > 0 && !splitMode && (
              <button onClick={() => initSplitMode(tableOrders.filter(o => !o.invoice_number && !o.is_invalid))}
                style={{ ...btn('primary'), padding: '8px 16px', fontSize: '0.85rem' }}>
                Split Invoice
              </button>
            )}
            {splitMode && (
              <button onClick={cancelSplit} style={{ ...btn('danger'), padding: '8px 16px', fontSize: '0.85rem' }}>
                Cancel Split
              </button>
            )}
            {unbilledCount > 0 && (
              <button onClick={() => openBillingModal(tableOrders.filter(o => !o.invoice_number && !o.is_invalid))}
                style={{ ...btn('primary'), padding: '8px 16px', fontSize: '0.85rem' }}>
                {splitMode ? 'Generate Invoices' : 'Generate Invoice'}
              </button>
            )}
          </div>
        </div>

        {splitMode && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '10px', padding: '10px 16px', marginBottom: '16px', fontSize: '0.88rem', color: '#92400e' }}>
            Split mode: assign each order to an invoice group using the dropdown, then click Generate Invoices.
          </div>
        )}

        {/* ── Active / Awaiting Payment orders ── */}
        {paidTodayOrders.length > 0 && unpaidOrders.length > 0 && (
          <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Active Orders
          </div>
        )}
        {tableOrders.length === 0 ? (
          <div style={{ ...card, padding: '40px', textAlign: 'center', color: '#999' }}>
            {view?.type === 'counter-session' ? 'No orders yet.' : 'No orders for this table today.'}
          </div>
        ) : unpaidOrders.length === 0 && paidTodayOrders.length > 0 ? null : (
          unpaidOrders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              onKOT={handleKOT}
              onReprint={o => setInvoicePrint({ order: o, mergedOrderNumbers: [] })}
              onCollectPayment={collectPayment}
              onDelete={deleteOrder}
              onMarkInvalid={markOrderInvalid}
              splitMode={splitMode}
              group={orderGroups[order.id] || 1}
              onGroupChange={setGroup}
              maxGroups={unbilledCount}
            />
          ))
        )}

        {/* ── Paid Today ── */}
        {paidTodayOrders.length > 0 && (
          <div style={{ marginTop: unpaidOrders.length > 0 ? '24px' : '0' }}>
            <div style={{ ...card, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>Paid Today</h2>
                <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: '12px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: '700' }}>
                  {paidTodayOrders.length}
                </span>
              </div>
              {paidTodayOrders.map(order => {
                const isVoid = !!order.is_invalid;
                return (
                <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem', opacity: isVoid ? 0.6 : 1 }}>
                  <div>
                    <div style={{ fontWeight: '600', color: isVoid ? '#999' : '#333', textDecoration: isVoid ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {order.invoice_number}
                      {isVoid && <span style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', textDecoration: 'none' }}>INVALID</span>}
                    </div>
                    <div style={{ color: '#888', fontSize: '0.78rem' }}>
                      {order.order_number && <span style={{ fontWeight: '600', color: '#555' }}>{order.order_number} · </span>}
                      {order.payment_method?.toUpperCase()} · {fmtDateTime(order.paid_at)}
                    </div>
                    {!isVoid && editingPaymentId === order.id && (
                      <div style={{ display: 'flex', gap: '5px', marginTop: '8px', flexWrap: 'wrap' }}>
                        {PAYMENT_METHODS.map(m => (
                          <button key={m} onClick={() => changePaymentMethod(order.id, m)} style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', border: 'none',
                            background: order.payment_method === m ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                            color: order.payment_method === m ? 'white' : '#555',
                          }}>{m.toUpperCase()}</button>
                        ))}
                        <button onClick={() => setEditingPaymentId(null)} style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', border: '1px solid #e5e7eb', background: 'white', color: '#888' }}>✕</button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <div style={{ fontWeight: '700', color: isVoid ? '#bbb' : '#16a34a', textDecoration: isVoid ? 'line-through' : 'none' }}>{fmt(order.total_payable)}</div>
                    {!isVoid && (
                      <>
                        <button
                          onClick={() => setEditingPaymentId(editingPaymentId === order.id ? null : order.id)}
                          style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#555', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                        >Edit Payment</button>
                        <button
                          onClick={() => setInvoicePrint({ order, mergedOrderNumbers: [] })}
                          style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#555', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                        >Print</button>
                        <button
                          onClick={() => { if (window.confirm(`Mark invoice ${order.invoice_number} as invalid? This cannot be undone.`)) markOrderInvalid(order.id); }}
                          style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: '6px', padding: '4px 10px', fontSize: '0.78rem', color: '#dc2626', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}
                        >Mark as Invalid</button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', fontWeight: '800', color: '#333' }}>
                <span>Total Collected</span>
                <span style={{ color: '#16a34a' }}>{fmt(paidTodayOrders.filter(o => !o.is_invalid).reduce((s, o) => s + (o.total_payable || 0), 0))}</span>
              </div>
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                {PAYMENT_METHODS.filter(m => paidTodayOrders.some(o => o.payment_method === m && !o.is_invalid)).map(m => (
                  <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#666', padding: '2px 0' }}>
                    <span>{m.toUpperCase()}</span>
                    <span style={{ fontWeight: '600' }}>{fmt(paidTodayOrders.filter(o => o.payment_method === m && !o.is_invalid).reduce((s, o) => s + (o.total_payable || 0), 0))}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {kotOrder && (
          <KOTPrint
            order={kotOrder}
            restaurant={restaurant}
            billingSettings={billingSettings}
            onClose={() => setKotOrder(null)}
          />
        )}
        {invoicePrint && (
          <InvoicePrint
            order={invoicePrint.order}
            mergedOrderNumbers={invoicePrint.mergedOrderNumbers}
            restaurant={restaurant}
            billingSettings={billingSettings}
            onClose={closeInvoicePrint}
          />
        )}

        {/* Billing modal — stays open until all groups are billed */}
        {billingModal && !invoicePrint && (
          <BillingModal
            groups={billingModal.groups}
            billingState={billingState}
            setBillingState={setBillingState}
            billingDefaults={billingDefaults}
            computeGroupBill={computeGroupBill}
            onBillGroup={processAndPrintGroup}
            onCollectPayment={collectPayment}
            restaurantId={restaurant.id}
            onClose={() => { setBillingModal(null); setSplitMode(false); setOrderGroups({}); }}
          />
        )}
      </div>
    );
  }

  // ── Counter Orders View ──────────────────────────────────────────────────────
  if (view?.type === 'counter') {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '30px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button onClick={() => { setSplitMode(false); setOrderGroups({}); setView('dashboard'); }}
            style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
            ← Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#333' }}>Counter Orders</h1>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => { setAddOrderPlacing(false); setAddOrderCart([]); setAddOrderNotes(''); setAddOrderSearch(''); setAddOrderCategory(''); setView({ type: 'add-order', context: {} }); }}
              style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
              + New Counter Order
            </button>
          </div>
        </div>

        {counterOrders.length === 0 ? (
          <div style={{ ...card, padding: '40px', textAlign: 'center', color: '#999' }}>
            No counter orders today.
          </div>
        ) : (
          counterOrders.map(order => (
            <div key={order.id} style={{ marginBottom: '10px' }}>
              <OrderCard
                order={order}
                onKOT={handleKOT}
                onReprint={o => setInvoicePrint({ order: o, mergedOrderNumbers: [] })}
                onCollectPayment={collectPayment}
                splitMode={false}
                group={1}
                onGroupChange={() => {}}
              />
              {!order.invoice_number && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-4px', marginBottom: '8px' }}>
                  <button
                    onClick={() => openBillingModal([order])}
                    style={{ ...btn('primary'), padding: '7px 16px', fontSize: '0.82rem' }}
                  >
                    Generate Invoice
                  </button>
                </div>
              )}
            </div>
          ))
        )}

        {kotOrder && (
          <KOTPrint order={kotOrder} restaurant={restaurant} billingSettings={billingSettings} onClose={() => setKotOrder(null)} />
        )}
        {invoicePrint && (
          <InvoicePrint
            order={invoicePrint.order}
            mergedOrderNumbers={invoicePrint.mergedOrderNumbers}
            restaurant={restaurant}
            billingSettings={billingSettings}
            onClose={closeInvoicePrint}
          />
        )}
        {billingModal && !invoicePrint && (
          <BillingModal
            groups={billingModal.groups}
            billingState={billingState}
            setBillingState={setBillingState}
            billingDefaults={billingDefaults}
            computeGroupBill={computeGroupBill}
            onBillGroup={processAndPrintGroup}
            onCollectPayment={collectPayment}
            restaurantId={restaurant.id}
            onClose={() => setBillingModal(null)}
          />
        )}
      </div>
    );
  }

  // ── Dashboard View ───────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%', padding: '30px 20px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#333' }}>POS Dashboard</h1>
        <button onClick={loadData} style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}>
          Refresh
        </button>
      </div>

      {/* Incoming QR orders — require manager confirmation before appearing on table */}
      {incomingOrders.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <style>{`@keyframes menuron-pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: '#FF6B00',
                animation: 'menuron-pulse 1.2s ease-in-out infinite',
              }} />
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#FF6B00', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Incoming Orders ({incomingOrders.length})
              </h2>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {incomingOrders.length > 1 && (
                <button
                  onClick={acknowledgeAll}
                  style={{ ...btn('green'), padding: '7px 16px', fontSize: '0.82rem' }}
                >
                  Acknowledge All
                </button>
              )}
              {incomingOverflows && (
                <button
                  onClick={() => navigate('/orders')}
                  style={{ ...btn('outline'), padding: '7px 16px', fontSize: '0.82rem' }}
                >
                  View All →
                </button>
              )}
            </div>
          </div>
          <div ref={incomingScrollRef} style={{ maxHeight: '430px', overflowY: 'auto', paddingRight: '4px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gridAutoRows: 'minmax(200px, auto)', gap: '14px' }}>
            {incomingOrders.map(order => {
              const tableName = order.restaurant_tables
                ? (order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`)
                : (order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Unknown Table');
              const time = fmtDateTime(order.created_at);
              const items = order.order_items || [];
              const waiterConfirmed = order.status === 'waiter_confirmed';
              const waiterPlaced = order.order_source === 'WAITER';
              const cardBg = waiterConfirmed ? '#f0fdf4' : '#fff7ed';
              const cardBorder = waiterConfirmed ? '#16a34a' : '#FF6B00';
              return (
                <div key={order.id} style={{
                  background: cardBg,
                  border: `2px solid ${cardBorder}`,
                  borderRadius: '14px', padding: '18px',
                  display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333' }}>{tableName}</div>
                    <div style={{ fontSize: '0.78rem', color: '#888' }}>{time}</div>
                  </div>
                  {waiterConfirmed && (
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#16a34a', marginBottom: '10px' }}>
                      {waiterPlaced ? `Placed by ${order.confirmed_by_name}` : `✓ Confirmed by ${order.confirmed_by_name}`}
                    </div>
                  )}
                  <div style={{ marginBottom: '14px', flex: 1 }}>
                    {items.map((oi, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#444', padding: '3px 0', borderBottom: idx < items.length - 1 ? `1px solid ${waiterConfirmed ? '#bbf7d0' : '#ffe4cc'}` : 'none' }}>
                        <span>{oi.menu_items?.name || 'Item'}</span>
                        <span style={{ fontWeight: '600', color: waiterConfirmed ? '#16a34a' : '#FF6B00' }}>× {oi.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => confirmOrder(order.id)}
                    style={{ ...btn(waiterConfirmed ? 'green' : 'primary'), width: '100%', padding: '10px', fontSize: '0.88rem', marginTop: 'auto' }}
                  >
                    {waiterConfirmed ? 'Acknowledge Order' : 'Confirm Order'}
                  </button>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}

      {/* Two-column layout: left = Tables + Counter, right = Today's Invoices */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '28px', alignItems: 'start' }}>

      {/* LEFT COLUMN */}
      <div>

      {/* Tables grid — active (orange filled) + available (neutral) in one flat grid */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Tables
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
          {tables.map(table => {
            const orders = tableOrdersMap[table.id] || [];
            const name = table.custom_name || `Table ${table.table_number}`;
            const unbilledOrders = orders.filter(o => !o.invoice_number && !o.is_invalid);
            const isActive = unbilledOrders.length > 0;
            const unbilledItems = unbilledOrders.reduce((s, o) => s + (o.order_items || []).reduce((ss, i) => ss + i.quantity, 0), 0);
            const unbilledAmount = unbilledOrders.reduce((s, o) => s + (o.total_amount || 0), 0);

            return (
              <div
                key={table.id}
                onClick={() => setView({ type: 'table', tableId: table.id })}
                style={{
                  borderRadius: '14px', cursor: 'pointer', transition: 'all 0.15s',
                  padding: isActive ? '18px' : '20px',
                  background: isActive ? '#FF6B00' : 'rgba(255,255,255,0.95)',
                  border: isActive ? 'none' : '1.5px solid #e5e7eb',
                  boxShadow: isActive ? '0 4px 16px rgba(255,107,0,0.25)' : '0 1px 4px rgba(0,0,0,0.06)',
                  minHeight: '110px',
                  display: 'flex', flexDirection: 'column', justifyContent: isActive ? 'space-between' : 'center',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.boxShadow = isActive
                    ? '0 8px 28px rgba(255,107,0,0.38)'
                    : '0 4px 16px rgba(0,0,0,0.1)';
                  if (!isActive) e.currentTarget.style.borderColor = '#FF6B00';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.boxShadow = isActive
                    ? '0 4px 16px rgba(255,107,0,0.25)'
                    : '0 1px 4px rgba(0,0,0,0.06)';
                  if (!isActive) e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                {isActive ? (
                  <>
                    <div style={{ fontWeight: '700', fontSize: '1.05rem', color: 'white' }}>{name}</div>
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: '800', color: 'white', lineHeight: 1 }}>
                        {fmt(unbilledAmount)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', marginTop: '5px', display: 'flex', gap: '10px' }}>
                        <span>{unbilledOrders.length} {unbilledOrders.length === 1 ? 'order' : 'orders'}</span>
                        <span>·</span>
                        <span>{unbilledItems} {unbilledItems === 1 ? 'item' : 'items'}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#999' }}>{name}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Counter Sessions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Counter Orders
          </h2>
          <button
            onClick={() => {
              const sessionId = crypto.randomUUID();
              const sessionNumber = orderedSessions.length + 1;
              setAddOrderPlacing(false); setAddOrderCart([]); setAddOrderNotes(''); setAddOrderSearch(''); setAddOrderCategory('');
              setView({ type: 'add-order', context: { sessionId, sessionNumber } });
            }}
            style={{ ...btn('primary'), padding: '8px 16px', fontSize: '0.85rem' }}
          >
            + New Counter Order
          </button>
        </div>
        {(() => {
          const activeSessions = orderedSessions.filter(id => sessionOrdersMap[id].some(o => !o.invoice_number));
          return activeSessions.length === 0 ? (
            <div style={{ ...card, padding: '32px', textAlign: 'center', color: '#bbb', fontSize: '0.95rem' }}>
              No active counter orders.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
              {activeSessions.map(sessionId => {
                const sessionOrders = sessionOrdersMap[sessionId] || [];
                const sessionNum = sessionNumberMap[sessionId];
                const unbilledSessionOrders = sessionOrders.filter(o => !o.invoice_number && !o.is_invalid);
                const unbilledAmount = unbilledSessionOrders.reduce((s, o) => s + (o.total_amount || 0), 0);
                const unbilledItems = unbilledSessionOrders.reduce((s, o) => s + (o.order_items || []).reduce((ss, i) => ss + i.quantity, 0), 0);
                return (
                  <div
                    key={sessionId}
                    onClick={() => setView({ type: 'counter-session', sessionId })}
                    style={{
                      borderRadius: '14px', cursor: 'pointer', transition: 'all 0.15s',
                      padding: '18px',
                      background: '#FF6B00',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(255,107,0,0.25)',
                      minHeight: '110px',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    }}
                    onMouseOver={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(255,107,0,0.38)'; }}
                    onMouseOut={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(255,107,0,0.25)'; }}
                  >
                    <div style={{ fontWeight: '700', fontSize: '1.05rem', color: 'white' }}>Counter {sessionNum}</div>
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: '800', color: 'white', lineHeight: 1 }}>
                        {fmt(unbilledAmount)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', marginTop: '5px', display: 'flex', gap: '10px' }}>
                        <span>{unbilledSessionOrders.length} {unbilledSessionOrders.length === 1 ? 'order' : 'orders'}</span>
                        <span>·</span>
                        <span>{unbilledItems} {unbilledItems === 1 ? 'item' : 'items'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
      </div>{/* END LEFT COLUMN */}

      {/* RIGHT COLUMN: Today's Invoices */}
      <div style={{ position: 'sticky', top: '20px' }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Today's Invoices
        </h2>
        {(() => {
          const billedOrders = activeOrders
            .filter(o => !!o.invoice_number && new Date(o.created_at) >= todayStart)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          if (billedOrders.length === 0) return (
            <div style={{ ...card, padding: '24px', textAlign: 'center', color: '#bbb', fontSize: '0.88rem' }}>
              No invoices yet today.
            </div>
          );
          return (
            <div style={{ ...card, padding: '0', overflow: 'hidden', maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
              {billedOrders.map((order, idx) => {
                const time = fmtDateTime(order.created_at);
                const tableName = order.restaurant_tables
                  ? (order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`)
                  : 'Counter';
                const isPaid = !!order.paid_at;
                const isVoid = !!order.is_invalid;
                return (
                  <div key={order.id} style={{
                    padding: '10px 14px',
                    borderBottom: idx < billedOrders.length - 1 ? '1px solid #f3f4f6' : 'none',
                    background: isVoid ? '#fafafa' : 'white',
                    opacity: isVoid ? 0.65 : 1,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.85rem', color: isVoid ? '#999' : '#333', textDecoration: isVoid ? 'line-through' : 'none' }}>{order.invoice_number}</span>
                      {isVoid ? (
                        <span style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem', fontWeight: '700' }}>INVALID</span>
                      ) : (
                        <span style={{
                          background: isPaid ? '#dcfce7' : '#fef3c7',
                          color: isPaid ? '#16a34a' : '#92400e',
                          border: `1px solid ${isPaid ? '#bbf7d0' : '#fcd34d'}`,
                          borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem', fontWeight: '700'
                        }}>{isPaid ? 'Paid' : 'Unpaid'}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.78rem', color: '#777', fontWeight: '600' }}>{tableName}</span>
                        <span style={{ fontSize: '0.72rem', color: '#bbb', marginLeft: '6px' }}>{time}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '0.85rem', color: isVoid ? '#bbb' : '#333', textDecoration: isVoid ? 'line-through' : 'none' }}>{fmt(order.total_payable || order.total_amount)}</span>
                        {!isVoid && (
                          <>
                            <button
                              onClick={() => setEditingPaymentId(editingPaymentId === order.id ? null : order.id)}
                              style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '2px 8px', fontSize: '0.72rem', color: '#555', cursor: 'pointer', fontWeight: '600' }}
                            >Edit</button>
                            <button
                              onClick={() => setInvoicePrint({ order, mergedOrderNumbers: [] })}
                              style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '2px 8px', fontSize: '0.72rem', color: '#555', cursor: 'pointer', fontWeight: '600' }}
                            >Print</button>
                            <button
                              onClick={() => { if (window.confirm(`Mark invoice ${order.invoice_number} as invalid? This cannot be undone.`)) markOrderInvalid(order.id); }}
                              style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: '5px', padding: '2px 8px', fontSize: '0.72rem', color: '#dc2626', cursor: 'pointer', fontWeight: '600' }}
                            >Mark as Invalid</button>
                          </>
                        )}
                      </div>
                    </div>
                    {!isVoid && editingPaymentId === order.id && (
                      <div style={{ display: 'flex', gap: '5px', marginTop: '8px', flexWrap: 'wrap' }}>
                        {PAYMENT_METHODS.map(m => (
                          <button key={m} onClick={() => changePaymentMethod(order.id, m)} style={{
                            padding: '3px 8px', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer', border: 'none',
                            background: order.payment_method === m ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                            color: order.payment_method === m ? 'white' : '#555',
                          }}>{m.toUpperCase()}</button>
                        ))}
                        <button onClick={() => setEditingPaymentId(null)} style={{ padding: '3px 6px', borderRadius: '5px', fontSize: '0.7rem', cursor: 'pointer', border: '1px solid #e5e7eb', background: 'white', color: '#888' }}>✕</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
      {/* END RIGHT COLUMN */}

      </div>{/* END TWO-COLUMN GRID */}

      {invoicePrint && (
        <InvoicePrint
          order={invoicePrint.order}
          mergedOrderNumbers={invoicePrint.mergedOrderNumbers}
          restaurant={restaurant}
          billingSettings={billingSettings}
          onClose={closeInvoicePrint}
        />
      )}
    </div>
  );
};

// ─── Billing modal ─────────────────────────────────────────────────────────────
// Each group has its own items breakdown + on-the-fly adjustment controls.
// Each "Bill & Print" button bills one group at a time.
const inputStyle = {
  padding: '6px 10px', border: '1.5px solid #e5e7eb', borderRadius: '8px',
  fontSize: '0.85rem', outline: 'none', background: 'white',
};
const labelStyle = { fontSize: '0.78rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' };


const BillingModal = ({ groups, billingState, setBillingState, billingDefaults, computeGroupBill, onBillGroup, onCollectPayment, restaurantId, onClose }) => {
  const allBilled = groups.every(g => billingState[g.groupNum]?.billed);

  const upd = (groupNum, field, value) => {
    setBillingState(prev => ({ ...prev, [groupNum]: { ...prev[groupNum], [field]: value } }));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1500, padding: '24px 16px', overflowY: 'auto',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', padding: '24px',
        width: '100%', maxWidth: '820px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
      }}>
        {/* Modal header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>
            {groups.length > 1 ? `Generate ${groups.length} Invoices` : 'Generate Invoice'}
          </h2>
          {groups.length > 1 && (
            <div style={{ fontSize: '0.82rem', color: '#888' }}>
              {groups.filter(g => billingState[g.groupNum]?.billed).length} / {groups.length} billed
            </div>
          )}
        </div>

        {groups.map(({ groupNum, orders: groupOrders }) => {
          const gs = billingState[groupNum] || {};
          const bill = computeGroupBill(groupOrders, gs);
          const isBilled = !!gs.billed;
          const isProcessing = !!gs.processing;
          const allItems = groupOrders.flatMap(o => o.order_items || []);

          // Configured defaults for hint display
          const cfgDiscount = billingDefaults?.defaultDiscountEnabled
            ? (billingDefaults.defaultDiscountType === 'PERCENT'
                ? `${billingDefaults.defaultDiscountValue}% off`
                : `₹${billingDefaults.defaultDiscountValue} off`)
            : 'None';
          const cfgGst = billingDefaults?.gstEnabled ? `${billingDefaults.gstPercent}%` : 'Disabled';
          const cfgSc = billingDefaults?.serviceChargeEnabled ? `${billingDefaults.serviceChargePercent}%` : 'Disabled';

          return (
            <div key={groupNum} style={{
              border: isBilled ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
              borderRadius: '12px', padding: '18px', marginBottom: '16px',
              background: isBilled ? '#f0fdf4' : 'white',
            }}>
              {isBilled ? (
                /* ── Billed: invoice summary + paid/unpaid badge ── */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#16a34a' }}>
                      ✓ {groups.length > 1 ? `Invoice ${groupNum} Generated` : 'Invoice Generated'}
                      <span style={{ fontWeight: '400', fontSize: '0.8rem', color: '#888', marginLeft: '8px' }}>
                        {groupOrders.map(o => o.order_number).join(', ')}
                      </span>
                    </div>
                    {gs.autoPaid ? (
                      <span style={{ background: '#16a34a', color: 'white', fontSize: '0.75rem', fontWeight: '700', padding: '3px 12px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                        PAID · {(gs.paymentMethod || 'cash').toUpperCase()}
                      </span>
                    ) : (
                      <span style={{ background: '#dc2626', color: 'white', fontSize: '0.75rem', fontWeight: '700', padding: '3px 12px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                        UNPAID
                      </span>
                    )}
                  </div>
                    {/* Items table */}
                    <div style={{ marginBottom: '12px', border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden', fontSize: '0.82rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                        {['Item', 'Qty', 'Rate', 'Total'].map((h, i) => (
                          <div key={h} style={{ padding: '5px 8px', fontWeight: '700', color: '#888', textAlign: i > 0 ? 'right' : 'left' }}>{h}</div>
                        ))}
                      </div>
                      {allItems.map((oi, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', borderBottom: idx < allItems.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                          <div style={{ padding: '6px 8px', color: '#333' }}>{oi.menu_items?.name || 'Item'}</div>
                          <div style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>{oi.quantity}</div>
                          <div style={{ padding: '6px 8px', color: '#555', textAlign: 'right' }}>₹{(oi.unit_price || 0).toFixed(0)}</div>
                          <div style={{ padding: '6px 8px', color: '#333', fontWeight: '600', textAlign: 'right' }}>₹{((oi.quantity || 0) * (oi.unit_price || 0)).toFixed(2)}</div>
                        </div>
                      ))}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <div style={{ padding: '6px 8px', fontWeight: '700', color: '#555', gridColumn: '1 / 4' }}>Subtotal</div>
                        <div style={{ padding: '6px 8px', fontWeight: '700', color: '#333', textAlign: 'right' }}>₹{bill.subtotal.toFixed(2)}</div>
                      </div>
                    </div>
                    {/* Bill amounts */}
                    <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px', fontSize: '0.85rem' }}>
                      {bill.discountAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#16a34a' }}>
                          <span>Discount</span><span>−₹{bill.discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {bill.discountAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                          <span>Taxable Amount</span><span>₹{bill.taxableAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {gs.gstEnabled && bill.gstAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                          <span>GST ({gs.gstPercent}%)</span><span>₹{bill.gstAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {gs.serviceChargeEnabled && bill.serviceChargeAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                          <span>Service Charge ({gs.serviceChargePercent}%)</span><span>₹{bill.serviceChargeAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#111', borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px' }}>
                        <span>Total Payable</span><span>₹{bill.totalPayable.toFixed(2)}</span>
                      </div>
                    </div>
                </div>
              ) : (
                /* ── Unbilled: single-column with adjustments ── */
                <>
                  {/* Group header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#FF6B00' }}>
                      {groups.length > 1 ? `Invoice ${groupNum}` : 'Invoice'}
                      <span style={{ fontWeight: '400', fontSize: '0.8rem', color: '#888', marginLeft: '8px' }}>
                        {groupOrders.map(o => o.order_number).join(', ')}
                      </span>
                    </div>
                  </div>

                  {/* Customer picker */}
                  <CustomerPicker
                    restaurantId={restaurantId}
                    value={gs.selectedCustomer}
                    onChange={(customer) => {
                      setBillingState(prev => ({
                        ...prev,
                        [groupNum]: {
                          ...prev[groupNum],
                          selectedCustomer: customer,
                          ...(customer && customer.discount_type !== 'NONE' ? {
                            discountType: customer.discount_type === 'FLAT' ? 'FIXED' : customer.discount_type,
                            discountValue: customer.discount_value || 0,
                          } : {}),
                        },
                      }));
                    }}
                    disabled={isProcessing}
                  />

                  {/* Items table */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={labelStyle}>Items</div>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden', fontSize: '0.82rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', gap: '0', background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                        {['Item', 'Qty', 'Rate', 'Total'].map((h, i) => (
                          <div key={h} style={{ padding: '6px 8px', fontWeight: '700', color: '#888', textAlign: i > 0 ? 'right' : 'left' }}>{h}</div>
                        ))}
                      </div>
                      {allItems.map((oi, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', gap: '0', borderBottom: idx < allItems.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                          <div style={{ padding: '7px 8px', color: '#333' }}>{oi.menu_items?.name || 'Item'}</div>
                          <div style={{ padding: '7px 8px', color: '#555', textAlign: 'right' }}>{oi.quantity}</div>
                          <div style={{ padding: '7px 8px', color: '#555', textAlign: 'right' }}>₹{(oi.unit_price || 0).toFixed(0)}</div>
                          <div style={{ padding: '7px 8px', color: '#333', fontWeight: '600', textAlign: 'right' }}>₹{((oi.quantity || 0) * (oi.unit_price || 0)).toFixed(2)}</div>
                        </div>
                      ))}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <div style={{ padding: '7px 8px', fontWeight: '700', color: '#555', gridColumn: '1 / 4' }}>Subtotal</div>
                        <div style={{ padding: '7px 8px', fontWeight: '700', color: '#333', textAlign: 'right' }}>₹{bill.subtotal.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Adjustments */}
                  <div style={{ marginBottom: '14px', background: '#fafafa', borderRadius: '10px', padding: '14px', border: '1px solid #f0f0f0' }}>
                    <div style={labelStyle}>Adjustments</div>
                    {/* Discount */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444', minWidth: '100px' }}>Discount</span>
                        <select
                          value={gs.discountType || 'NONE'}
                          onChange={e => upd(groupNum, 'discountType', e.target.value)}
                          disabled={isProcessing}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          <option value="NONE">No Discount</option>
                          <option value="PERCENT">% Off</option>
                          <option value="FIXED">Flat Off (₹)</option>
                        </select>
                        {gs.discountType && gs.discountType !== 'NONE' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              type="number" min="0" step="any"
                              value={gs.discountValue || 0}
                              onChange={e => upd(groupNum, 'discountValue', parseFloat(e.target.value) || 0)}
                              disabled={isProcessing}
                              style={{ ...inputStyle, width: '70px' }}
                            />
                            <span style={{ fontSize: '0.82rem', color: '#888' }}>{gs.discountType === 'PERCENT' ? '%' : '₹'}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '3px', marginLeft: '108px' }}>Configured: {cfgDiscount}</div>
                    </div>
                    {/* GST */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444', minWidth: '100px' }}>GST</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={!!gs.gstEnabled}
                            onChange={e => upd(groupNum, 'gstEnabled', e.target.checked)}
                            disabled={isProcessing}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#555' }}>Apply</span>
                        </label>
                        {gs.gstEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              type="number" min="0" max="100" step="0.01"
                              value={gs.gstPercent ?? 0}
                              onChange={e => upd(groupNum, 'gstPercent', parseFloat(e.target.value) || 0)}
                              disabled={isProcessing}
                              style={{ ...inputStyle, width: '64px' }}
                            />
                            <span style={{ fontSize: '0.82rem', color: '#888' }}>%</span>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '3px', marginLeft: '108px' }}>Configured: {cfgGst}</div>
                    </div>
                    {/* Service Charge */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444', minWidth: '100px' }}>Svc Charge</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={!!gs.serviceChargeEnabled}
                            onChange={e => upd(groupNum, 'serviceChargeEnabled', e.target.checked)}
                            disabled={isProcessing}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.85rem', color: '#555' }}>Apply</span>
                        </label>
                        {gs.serviceChargeEnabled && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              type="number" min="0" max="100" step="0.01"
                              value={gs.serviceChargePercent ?? 0}
                              onChange={e => upd(groupNum, 'serviceChargePercent', parseFloat(e.target.value) || 0)}
                              disabled={isProcessing}
                              style={{ ...inputStyle, width: '64px' }}
                            />
                            <span style={{ fontSize: '0.82rem', color: '#888' }}>%</span>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '3px', marginLeft: '108px' }}>Configured: {cfgSc}</div>
                    </div>
                  </div>

                  {/* Bill summary */}
                  <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '0.88rem' }}>
                    {bill.discountAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#16a34a' }}>
                        <span>Discount</span><span>−₹{bill.discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {bill.discountAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                        <span>Taxable Amount</span><span>₹{bill.taxableAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {gs.gstEnabled && bill.gstAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                        <span>GST ({gs.gstPercent}%)</span><span>₹{bill.gstAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {gs.serviceChargeEnabled && bill.serviceChargeAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#555' }}>
                        <span>Service Charge ({gs.serviceChargePercent}%)</span><span>₹{bill.serviceChargeAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#111', borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px' }}>
                      <span>Total Payable</span><span>₹{bill.totalPayable.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Payment mode */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Payment Mode</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {['unpaid', ...PAYMENT_METHODS].map(m => (
                        <button
                          key={m}
                          onClick={() => upd(groupNum, 'paymentMethod', m)}
                          disabled={isProcessing}
                          style={{
                            border: '2px solid',
                            borderColor: gs.paymentMethod === m ? (m === 'unpaid' ? '#6b7280' : '#FF6B00') : '#e5e7eb',
                            background: gs.paymentMethod === m ? (m === 'unpaid' ? '#f3f4f6' : '#fff7ed') : 'white',
                            color: gs.paymentMethod === m ? (m === 'unpaid' ? '#374151' : '#FF6B00') : '#555',
                            borderRadius: '8px', padding: '6px 14px',
                            fontWeight: gs.paymentMethod === m ? '700' : '500',
                            fontSize: '0.82rem', cursor: isProcessing ? 'not-allowed' : 'pointer',
                            textTransform: 'capitalize',
                          }}
                        >{m === 'unpaid' ? 'Unpaid' : m}</button>
                      ))}
                    </div>
                  </div>

                  {/* Generate Invoice button */}
                  {gs.error && (
                    <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '8px' }}>{gs.error}</div>
                  )}
                  <button
                    onClick={() => onBillGroup({ groupNum, orders: groupOrders })}
                    disabled={isProcessing}
                    style={{
                      width: '100%', border: 'none', borderRadius: '10px', padding: '12px',
                      fontSize: '0.92rem', fontWeight: '700',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      background: isProcessing ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      color: isProcessing ? '#9ca3af' : 'white',
                    }}
                  >
                    {isProcessing ? 'Processing...' : `Generate Invoice${groups.length > 1 ? ` ${groupNum}` : ''}`}
                  </button>
                </>
              )}
            </div>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ border: '2px solid #e5e7eb', borderRadius: '10px', padding: '10px 24px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', background: 'transparent', color: '#666' }}
          >
            {allBilled ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default POSDashboard;
